(() => {
  const signalR = require('@microsoft/signalr');

  angular.module('app').controller('common.views.aceClaims.index', AceClaimsPageController);

  AceClaimsPageController.$inject = [
    '$scope',
    '$stateParams',
    '$state',
    '$timeout',
    '$uibModal',
    'abp.services.app.aceClaims',
    'Hms.Ace.AceStatus',
    'Hms.PatientTickets.PatientTicketType',
    'moment',
    'appConstants',
  ];

  function AceClaimsPageController(
    $scope,
    $stateParams,
    $state,
    $timeout,
    $uibModal,
    aceClaimsSvc,
    enumAceStatus,
    enumPatientTicketType,
    moment,
    appConstants
  ) {
    const vm = this;
    vm.loading = 0;

    vm.enums = {
      aceStatus: enumAceStatus,
      patientTicketType: enumPatientTicketType,
    };

    vm.claimTypes = [];

    vm.requestParams = {
      claimNumber: $stateParams.claimNumber,
      ticketNumber: $stateParams.ticketNumber,
      claimServiceType: $stateParams.claimServiceType,
      skipCount: $stateParams.skipCount || 0,
      maxResultCount: $stateParams.maxResultCount || appConstants.grid.defaultPageSize,
      sorting: $stateParams.sorting,
    };

    // Every AceClaims row is a reimbursement claim by construction (HM-11104 registration
    // guard) — the backend GetAceClaimsInput has no claim-type filter, so this is a fixed
    // single-option display only, not sent with the query.
    vm.claimTypeDisplayValue = enumPatientTicketType.Reimbursement.id;

    vm.dateRangeOptions = App.createDateRangePickerOptions();
    vm.submissionDateRangeModel = {
      startDate: $stateParams.submissionDateFrom ? moment($stateParams.submissionDateFrom) : null,
      endDate: $stateParams.submissionDateTo ? moment($stateParams.submissionDateTo) : null,
    };

    vm.gridOptions = {
      appScopeProvider: vm,
      paginationPageSizes: appConstants.grid.defaultPageSizes,
      paginationPageSize: appConstants.grid.defaultPageSize,
      paginationCurrentPage: 0,
      useExternalPagination: true,
      useExternalSorting: true,
      resizable: true,
      columnDefs: [
        {
          name: 'actions',
          displayName: App.localize('Actions'),
          enableSorting: false,
          width: 100,
          headerCellTemplate: '<span></span>',
          cellTemplate: 'aceClaimActionsTemplate',
          allowCellFocus: false,
        },
        {
          displayName: App.localize('ClaimNumber'),
          field: 'claimNumber',
          minWidth: 100,
        },
        {
          displayName: App.localize('TicketNumber'),
          field: 'ticketNumber',
          minWidth: 100,
          cellTemplate: 'ticketNumberTemplate',
        },
        {
          displayName: App.localize('AceStatus'),
          field: 'aceStatus',
          minWidth: 110,
          cellTemplate: 'aceStatusTemplate',
        },
        {
          // Sort on the id (claimServiceType) — the name is resolved after the query, so it
          // is not a server-side sort key. The cell shows claimServiceTypeName.
          displayName: App.localize('ClaimServiceType'),
          field: 'claimServiceType',
          minWidth: 130,
          cellTemplate: 'claimServiceTypeTemplate',
        },
        {
          displayName: App.localize('MemberId'),
          field: 'memberId',
          minWidth: 250,
        },
        {
          displayName: App.localize('AceSubmissionDate'),
          field: 'aceSubmissionDate',
          minWidth: 140,
          cellFilter: "momentFormat: 'L'",
        },
        {
          displayName: App.localize('Company'),
          field: 'companyName',
          minWidth: 120,
        },
      ],
      onRegisterApi(gridApi) {
        $scope.gridApi = gridApi;
        $scope.gridApi.core.on.sortChanged($scope, (scope, callback) => {
          vm.requestParams.sorting =
            callback.length && callback[0].field
              ? `${callback[0].field} ${callback[0].sort.direction}`
              : null;
          vm.getAceClaims();
        });
        gridApi.pagination.on.paginationChanged(
          $scope,
          App.setPagination(vm.requestParams, vm.getAceClaims)
        );
      },
      data: [],
    };

    vm.getAceClaims = getAceClaims;
    vm.search = search;
    vm.openRegisterModal = openRegisterModal;
    vm.getClaimStatus = getClaimStatus;
    vm.getFullClaimDetails = fetchFullClaimDetails;
    vm.viewResults = viewResults;
    vm.retryAnalysis = retryAnalysis;
    vm.reRunAnalysis = reRunAnalysis;

    init();

    function init() {
      $timeout(() => {
        vm.gridOptions.paginationCurrentPage = Math.floor(
          vm.requestParams.skipCount / vm.requestParams.maxResultCount + 1
        );
      });

      // Source the Claim Service Type options from claim types that actually appear among ACE
      // claims (not the whole Claims table), so every filter option returns rows.
      aceClaimsSvc.getAceClaimServiceTypes().success((data) => {
        vm.claimTypes = data.items;
      });

      startStatusHubConnection();
    }

    function startStatusHubConnection() {
      // Live badge push (HM-11150): the portal broadcasts committed ACE status changes on this
      // hub. Best effort only — without it the badge is still correct on the next load or
      // manual refresh. Auth is the same OIDC bearer token every backend call uses; on the
      // WebSocket transport the client sends it as the access_token query parameter, which the
      // backend maps for /signalr paths only.
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${abp.appPath}signalr/ace-claims`, {
          accessTokenFactory: () => App.getAccessToken(),
        })
        .withAutomaticReconnect()
        .build();

      connection.on('aceClaimStatusUpdated', (message) => {
        $timeout(() => applyStatusUpdate(message));
      });

      connection.start().catch(() => {
        // Refused or unreachable — the listing works without live badges.
      });

      $scope.$on('$destroy', () => {
        connection.stop();
      });
    }

    function applyStatusUpdate(message) {
      if (!message || !message.claimNumber) return;

      const rows = vm.gridOptions.data || [];
      const row = rows.find(
        (r) => (r.claimNumber || '').toUpperCase() === message.claimNumber.toUpperCase()
      );
      if (!row) return;

      row.aceStatus = message.aceStatus;

      // The push always carries the key (null when there are no remarks); only a malformed
      // message omits it, and that must not blank a tooltip the row already has.
      if (!_.isUndefined(message.failureRemarks)) {
        row.failureRemarks = message.failureRemarks;
      }
    }

    function search() {
      vm.requestParams.skipCount = 0;
      vm.gridOptions.paginationCurrentPage = 1;
      vm.getAceClaims();
    }

    function openRegisterModal() {
      // Shared with the modal so the refresh decision survives every exit path — the Close button
      // resolves the modal, but Escape and the header dismiss reject it, and neither carries a
      // return value. Opening and cancelling without registering leaves the grid alone.
      const registerState = { shouldRefresh: false };

      $uibModal
        .open({
          templateUrl: require('./registerClaims/registerClaims.modal.html'),
          controller: 'common.views.aceClaims.registerClaimsModal as vm',
          backdrop: 'static',
          size: 'lg',
          resolve: {
            registerState: () => registerState,
          },
        })
        .result.then(onRegisterModalClosed, onRegisterModalClosed);

      function onRegisterModalClosed() {
        if (registerState.shouldRefresh) {
          vm.getAceClaims();
        }
      }
    }

    function formatDateRangeModel() {
      return {
        submissionDateFrom: vm.submissionDateRangeModel.startDate
          ? vm.submissionDateRangeModel.startDate.format('YYYY-MM-DD')
          : null,
        submissionDateTo: vm.submissionDateRangeModel.endDate
          ? vm.submissionDateRangeModel.endDate.format('YYYY-MM-DD')
          : null,
      };
    }

    function getRequestInput() {
      const input = $.extend({}, vm.requestParams, formatDateRangeModel());
      registerStateParams();
      return input;
    }

    function getAceClaims() {
      const input = getRequestInput();

      vm.loading += 1;
      aceClaimsSvc
        .getAceClaims(input)
        .success((data) => {
          vm.gridOptions.totalItems = data.totalCount;
          vm.gridOptions.data = data.items;
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    function getClaimStatus(row) {
      vm.loading += 1;
      aceClaimsSvc
        .refreshClaimStatus({ claimNumber: row.claimNumber })
        .success(getAceClaims)
        .finally(() => {
          vm.loading -= 1;
        });
    }

    function registerClaim(row) {
      vm.loading += 1;
      aceClaimsSvc
        .registerClaims({ claimNumbers: row.claimNumber })
        .success(getAceClaims)
        .finally(() => {
          vm.loading -= 1;
        });
    }

    function retryAnalysis(row) {
      registerClaim(row);
    }

    function reRunAnalysis(row) {
      registerClaim(row);
    }

    function fetchFullClaimDetails(row) {
      vm.loading += 1;
      aceClaimsSvc
        .fetchFullClaimDetails({ claimNumber: row.claimNumber })
        .success(() => {
          getAceClaims();
          // Fetch first so the stored result exists before the analysis view reads it.
          window.open(
            $state.href('aceClaimFullAnalysis', { claimNumber: row.claimNumber }),
            '_blank'
          );
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    function viewResults(row) {
      $state.go('aceClaimFullAnalysis', { claimNumber: row.claimNumber });
    }

    function registerStateParams() {
      const dateRangeModel = formatDateRangeModel();

      $state.transitionTo(
        $state.current,
        {
          claimNumber: vm.requestParams.claimNumber,
          ticketNumber: vm.requestParams.ticketNumber,
          claimServiceType: vm.requestParams.claimServiceType,
          submissionDateFrom: dateRangeModel.submissionDateFrom,
          submissionDateTo: dateRangeModel.submissionDateTo,
          skipCount: vm.requestParams.skipCount,
          maxResultCount: vm.requestParams.maxResultCount,
          sorting: vm.requestParams.sorting,
        },
        { notify: false }
      );
    }
  }
})();
